'use client';

/**
 * The one prompt shown when a session expires mid-task (F19).
 *
 * Mounted once in layout.tsx. The api-client holds any write that cannot be
 * replayed on its own (lib/session-recovery.ts) and this dialog decides its fate:
 * sign in again in a new tab, then Retry, or Cancel. The page underneath is never
 * navigated away, so whatever the user was editing is still there.
 */

import React, { useEffect, useState } from 'react';
import { LogIn, RotateCw } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  cancelSessionRecovery,
  getSessionRecoveryState,
  retrySessionRecovery,
  subscribeSessionRecovery,
  type SessionRecoveryState,
} from '@/lib/session-recovery';

export function SessionRecoveryDialog() {
  const [state, setState] = useState<SessionRecoveryState>(getSessionRecoveryState());
  const [signInOpened, setSignInOpened] = useState(false);

  useEffect(() => subscribeSessionRecovery(setState), []);
  useEffect(() => { if (!state.open) setSignInOpened(false); }, [state.open]);

  const openSignIn = () => {
    // A new tab keeps this page, and its unsaved draft, exactly as it is.
    // logout=1 clears the dead cookies for the sign-in; reason shows why.
    window.open('/login?reason=session_refresh&logout=1', '_blank', 'noopener');
    setSignInOpened(true);
  };

  const pending = state.waiting;
  const pendingText = pending === 1 ? 'One change is waiting to be sent.' : pending > 1 ? `${pending} changes are waiting to be sent.` : '';

  return (
    <Dialog open={state.open} onOpenChange={(open) => { if (!open) cancelSessionRecovery(); }}>
      <DialogContent className="sm:max-w-md" data-testid="session-recovery-dialog">
        <DialogHeader>
          <DialogTitle>{state.renewed ? 'Your session was renewed' : 'Your session expired'}</DialogTitle>
          <DialogDescription>
            {state.renewed
              ? 'Select Retry to send your change again. It was not sent automatically, so nothing is saved twice.'
              : 'Sign in again to continue. Your changes are kept on this page.'}
          </DialogDescription>
        </DialogHeader>

        {!state.renewed && (
          <ol className="list-decimal space-y-1 pl-5 text-[13px] text-uzo-mut">
            <li>Select Sign in again. The sign-in page opens in a new tab.</li>
            <li>Sign in there, then come back to this tab.</li>
            <li>Select Retry{pending ? ' to send what you were saving' : ''}.</li>
          </ol>
        )}
        {pendingText && <p className="text-[12px] text-uzo-mut">{pendingText}</p>}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" size="sm" onClick={() => cancelSessionRecovery()}>
            {pending ? 'Cancel' : 'Close'}
          </Button>
          {!state.renewed && (
            <Button variant={signInOpened ? 'outline' : 'default'} size="sm" onClick={openSignIn}>
              <LogIn className="mr-1.5 h-4 w-4" aria-hidden="true" /> Sign in again
            </Button>
          )}
          <Button
            size="sm"
            variant={state.renewed || signInOpened ? 'default' : 'outline'}
            onClick={() => retrySessionRecovery()}
          >
            <RotateCw className="mr-1.5 h-4 w-4" aria-hidden="true" /> Retry
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default SessionRecoveryDialog;
